import { Component, inject, input, ChangeDetectionStrategy } from '@angular/core';

import moment from 'moment-timezone';

import { I18nService } from '@core/i18n-legacy';
import type { JobSchedulerData } from '@core/offers-types';
import { IconComponent, RtTooltip } from '@core/user-interface-legacy';

import { JobSchedulerErrorComponent } from '../job-scheduler-error/job-scheduler-error.component';

type TemplateData = {
  tooltipContent: string;
  content: string;
};
@Component({
  selector: 'rt-job-scheduler-info',
  templateUrl: './job-scheduler-info.component.html',
  styleUrls: ['./job-scheduler-info.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RtTooltip, IconComponent, JobSchedulerErrorComponent],
})
export class JobSchedulerInfoComponent {
  private readonly i18n = inject(I18nService);

  private readonly SDAY_FORMAT = 'DD MMM YYYY';

  public readonly schedulerData = input.required<JobSchedulerData>();

  public translations: { [key: string]: string } = {};

  public get hasError(): boolean {
    return !!this.schedulerData()?.error;
  }

  public get isPublishDate(): boolean {
    return !!this.schedulerData()?.publishDate;
  }

  public get isCloseDate(): boolean {
    return !!this.schedulerData()?.closeDate;
  }

  private get formattedPublishDate(): string {
    const schedulerData = this.schedulerData();
    return schedulerData?.publishDate ? this.getFormattedDay(schedulerData.publishDate) : '';
  }

  private get formattedCloseDate(): string {
    const schedulerData = this.schedulerData();
    return schedulerData?.closeDate ? this.getFormattedDay(schedulerData?.closeDate) : '';
  }

  public ngOnInit(): void {
    this.prepareTranslations();
  }

  public get templateData(): TemplateData {
    if (this.isPublishDate && !this.isCloseDate) {
      return {
        tooltipContent: this.translations.publishScheduled,
        content: this.formattedPublishDate,
      };
    }
    if (this.isCloseDate && !this.isPublishDate) {
      return {
        tooltipContent: this.translations.closeScheduled,
        content: this.formattedCloseDate,
      };
    } else {
      return {
        tooltipContent: this.translations.jobScheduled,
        content: `${this.formattedPublishDate} - ${this.formattedCloseDate}`,
      };
    }
  }

  private getFormattedDay(eventDate: string): string {
    return moment(eventDate).format(this.SDAY_FORMAT);
  }

  private prepareTranslations(): void {
    const schedulerData = this.schedulerData();
    this.translations = {
      closeScheduled: this.i18n.translate('app.job_scheduler.schedule_info.close_scheduled', {
        closeAt: this.formattedCloseDate,
        timezone: schedulerData?.timezone,
      }),
      jobScheduled: this.i18n.translate('app.job_scheduler.schedule_info.job_scheduled', {
        closeAt: this.formattedCloseDate,
        publishAt: this.formattedPublishDate,
        timezone: schedulerData?.timezone,
      }),
      publishScheduled: this.i18n.translate('app.job_scheduler.schedule_info.publish_scheduled', {
        publishAt: this.formattedPublishDate,
        timezone: schedulerData?.timezone,
      }),
    };
  }
}
